import { VENDORS } from 'components/common/vendorConfig';
import { Label, Card, Checkbox } from 'semantic-ui-react';
import type { FoodCatalogBundle } from 'types';

/** One bundle's multiselect state, keyed by `bundleId`. */
type CheckedBundleMap = Record<string, boolean | undefined>;

/**
 * What one bundle card accepts.
 *
 * Established from its single JSX call site, `../index.tsx:167`.
 *
 * `programMAXMealEquivalents` is passed there (line 174) and read nowhere here.
 */
type FoodBundleCardProps = {
  /**
   * The bundle this card represents. `FoodCatalogBundle` rather than `FoodItem`
   * because the card renders bundle-level labels alongside the item-level ones.
   */
  bundle: FoodCatalogBundle;
  /**
   * Strips the vendor prefix off a bundle id for display. Called unguarded, and
   * defined at `../index.tsx:70`.
   */
  bundleName: (bundleId?: string) => string | undefined;
  /** Which bundles are ticked; indexed unguarded by this bundle's id. */
  checkedBundle: CheckedBundleMap;
  /** Selects this bundle as the one whose items are listed. Invoked unguarded. */
  onBundleSelect: (bundle: FoodCatalogBundle) => void;
  /**
   * Present enables the multiselect checkbox for multiselect vendors.
   *
   * The three explicit `| undefined`s on this type are required under
   * `exactOptionalPropertyTypes`: `../index.tsx` assigns each of them
   * unconditionally from its own optional props and state, so the property is
   * always PRESENT and sometimes holds `undefined`.
   */
  patientId?: string | null | undefined;
  /** Accepted but unread — see the type doc above. */
  programMAXMealEquivalents?: number | undefined;
  /**
   * Toggles this bundle in the multiselect map.
   *
   * Declared as `React.Dispatch<React.SetStateAction<...>>` rather than the
   * narrower `(v: T) => void` because `handleBundleCheck` below calls it with an
   * updater function; the narrow form would forbid that call.
   */
  setCheckedBundle: React.Dispatch<React.SetStateAction<CheckedBundleMap>>;
  /**
   * The currently selected bundle's id; equality tints this card.
   *
   * `null` is in the domain, not just `undefined`: the caller passes
   * `selectedBundle && selectedBundle.bundleId` and `selectedBundle` starts as
   * `null`. The distinction matters — `bundleId` is itself optional, so collapsing
   * the unselected case to `undefined` would make a bundle that carries no
   * `bundleId` compare equal and render as selected.
   */
  selectedBundleId?: string | null | undefined;
};

function FoodBundleCard(props: FoodBundleCardProps) {
  const bundle = props.bundle;
  const MULTISELECT_VENDORS = [VENDORS.MF, VENDORS.NU, VENDORS.CJ];

  const handleBundleCheck = (event: unknown, bundleId: string) => {
    props.setCheckedBundle((prevCheckedBundle) => ({ ...prevCheckedBundle, [bundleId]: !prevCheckedBundle[bundleId] }));
  };

  return (
    <Card
      link={true}
      onClick={() => props.onBundleSelect(bundle)}
      fluid
      style={{
        backgroundColor: props.selectedBundleId === bundle.bundleId ? 'aliceblue' : 'white',
        padding: '2px',
        borderRadius: '12px',
        margin: '12px 10px 0px 0px',
        boxShadow: '0 2px 5px 0 rgba(0, 0, 0, 0.16), 0 2px 10px 0 rgba(0, 0, 0, 0.12)',
      }}
    >
      <Card.Content>
        <Card.Header>
          {/* Bundle Multiselect */}
          {bundle.vendorId && MULTISELECT_VENDORS.includes(bundle.vendorId) && props.patientId && (
            <Checkbox
              // The `?? false` is unreachable rather than a new default: the
              // catalogue's `onSuccess` seeds this map with `false` for EVERY
              // bundle it fetched, and these cards render from that same array, so
              // the key is always present. It is here because the map's type is
              // open — which is the truth for a runtime-keyed read — and because
              // `checked={undefined}` would hand Semantic an UNCONTROLLED checkbox,
              // a real behaviour change if it ever did fire.
              checked={props.checkedBundle[String(bundle.bundleId)] ?? false}
              id={props.bundleName(bundle.bundleId) + props.patientId}
              floated="left"
              onClick={(event, _data) => handleBundleCheck(event, bundle.bundleId!)}
            />
          )}
          {'  '}
          {props.bundleName(bundle.bundleId)}{' '}
        </Card.Header>
        <Card.Meta>{bundle.bundleName}</Card.Meta>
        <Card.Description>
          {/* Meal Equivalents for MF: not done yet, PK: Implemented, Sunbasket: Undetermined, dont show anything */}
          {bundle.vendorId &&
          (bundle.vendorId === VENDORS.EF ||
            bundle.vendorId === VENDORS.MF ||
            bundle.vendorId === VENDORS.MH ||
            bundle.vendorId === VENDORS.ML ||
            bundle.vendorId === VENDORS.PK) ? (
            <>
              {bundle.mealEquivalents ? (
                <Label basic>MEQ: {`${bundle.mealEquivalents}`}</Label>
              ) : (
                <Label basic>MEQ: 0 </Label>
              )}
            </>
          ) : (
            ''
          )}

          {bundle.bundleTypeName && <Label basic>{`${bundle.bundleTypeName}`}</Label>}
          {bundle.vendorId && bundle.vendorId === 'SB' && (
            <>
              <Label basic>1 Meal Kit = 2 MEQ</Label>
            </>
          )}

          {bundle.bundlePrice && bundle.bundlePrice !== '' && (
            <>
              <Label basic>Price: ${`${bundle.bundlePrice}`}</Label>
            </>
          )}
          {bundle.flavorName && <Label basic>{`${bundle.flavorName}`}</Label>}
          {bundle.noOfItems && <Label basic>{bundle.noOfItems} Items</Label>}
          {bundle.valueUnits && bundle.valueUnitType && (
            <Label basic>{`${bundle.valueUnits} ${bundle.valueUnitType}`}</Label>
          )}
        </Card.Description>
      </Card.Content>
    </Card>
  );
}

export default FoodBundleCard;
